// Market-specific free shipping from the Market Shipping Threshold app (app proxy).
// window.ShippingThreshold.evaluate(cart) -> Promise<{ free, threshold, remaining, currency } | null>
// `cart` is the /cart.js JSON. Amounts are cart-currency subunits (5000 = 50.00).
// null = no threshold for this market/cart, or the app is unavailable: hide the bar.
(() => {
  if (window.ShippingThreshold) return;
  const ENDPOINT = "/apps/shipping-threshold";
  const TIMEOUT_MS = 4000;
  let memo = { key: null, promise: null };

  const currentCountry = () => {
    const settings = (window.theme && window.theme.settings) || {};
    const country = settings.country;
    return typeof country === "string" && /^[A-Za-z]{2}$/.test(country)
      ? country.toUpperCase()
      : null;
  };
  const currentRate = () => {
    const rate = Number(
      window.Shopify && window.Shopify.currency && window.Shopify.currency.rate,
    );
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  };
  const isAmount = (value) => Number.isInteger(value) && value >= 0;
  const isResult = (data) =>
    data !== null &&
    typeof data === "object" &&
    typeof data.free === "boolean" &&
    isAmount(data.threshold) &&
    isAmount(data.remaining) &&
    typeof data.currency === "string";

  const request = async (country, body) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(
        `${ENDPOINT}?country=${encodeURIComponent(country)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify(body),
          signal: controller.signal,
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = JSON.parse(await response.text());
      return { ok: true, value: isResult(data) ? data : null };
    } catch (error) {
      return { ok: false, value: null };
    } finally {
      clearTimeout(timer);
    }
  };

  const evaluate = (cart) => {
    const country = currentCountry();
    const rate = currentRate();
    const items = (cart && Array.isArray(cart.items) && cart.items) || [];
    // Gift cards / digital items don't ship, so they don't decide the threshold.
    const variantIds = items
      .filter((item) => item.requires_shipping !== false)
      .map((item) => item.variant_id);
    if (!country || !rate || !cart || variantIds.length === 0)
      return Promise.resolve(null);

    // Discounts already on the cart decide whether a free-shipping discount can still combine.
    const lineApps = items.flatMap((item) =>
      (item.line_level_discount_allocations || []).map(
        (a) => a.discount_application || {},
      ),
    );
    const cartApps = cart.cart_level_discount_applications || [];
    // Live /cart.js reports automatic discounts with type "" (not "automatic"), so only
    // "discount_code" counts as a code; anything else is treated as automatic.
    const isCode = (a) => a.type === "discount_code";
    const body = {
      currency: cart.currency,
      rate: rate,
      total: cart.total_price,
      variant_ids: variantIds,
      product_discount: lineApps.some((a) => !isCode(a)),
      order_discount: cartApps.some((a) => !isCode(a)),
      discount_code: lineApps.concat(cartApps).some(isCode),
    };
    const key = [
      country,
      body.currency,
      rate,
      body.total,
      variantIds.slice().sort().join(","),
      body.product_discount,
      body.order_discount,
      body.discount_code,
    ].join("|");
    if (memo.key === key) return memo.promise;

    const promise = request(country, body).then((result) => {
      // Errors are not memoized, so the next cart update retries.
      if (!result.ok && memo.promise === promise)
        memo = { key: null, promise: null };
      return result.value;
    });
    memo = { key: key, promise: promise };
    return promise;
  };

  window.ShippingThreshold = { evaluate: evaluate };
})();
